import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../services/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  Drawer, List, ListItem, ListItemIcon, ListItemText, Button, Typography, Box, Avatar, Divider,
  AppBar, Toolbar, IconButton, CssBaseline
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import ReportIcon from '@mui/icons-material/Report';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import BadgeIcon from '@mui/icons-material/Badge';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PunchClockIcon from '@mui/icons-material/PunchClock';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import InventoryIcon from '@mui/icons-material/Inventory';
import EditIcon from '@mui/icons-material/Edit';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'; // Ensure these are imported
import styles from '../styles/App.module.css';

const drawerWidth = 240;

const Navbar = (props) => {
  const { window } = props;
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Profile Edit State
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [newName, setNewName] = useState('');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      } else {
        setUserData(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await auth.signOut();
    localStorage.removeItem('token');
    navigate('/');
  };

  const hasRole = (roles) => {
    return userData && roles.includes(userData.papel);
  };

  const handleEditProfile = () => {
    setNewName(userData?.nome || '');
    setOpenProfileDialog(true);
  };

  const handleSaveProfile = async () => {
    if (!newName.trim()) return;
    try {
      const userRef = doc(db, 'tb_usuarios', auth.currentUser.uid);
      await updateDoc(userRef, { nome: newName });
      setUserData({ ...userData, nome: newName });
      setOpenProfileDialog(false);
      alert('Nome atualizado com sucesso!');
    } catch (error) {
      console.error("Erro ao atualizar perfil", error);
      alert("Erro ao atualizar perfil.");
    }
  };

  const drawerContent = (
    <div>
      <Typography variant="h6" className={styles.navbarTitle} sx={{ p: 2, textAlign: 'center', color: 'white', fontWeight: 'bold' }}>
        Predioo
      </Typography>
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />

      <List sx={{ pt: 0 }}>
        {/* --- GERAL --- */}
        {hasRole(['sindico', 'sub_sindico', 'conselheiro', 'morador', 'zelador']) && (
          <ListItem button component={Link} to="/dashboard" className={styles.navbarList}>
            <ListItemIcon><HomeIcon className={styles.navbarIcons} /></ListItemIcon>
            <ListItemText primary="Dashboard" className={styles.navbarList} />
          </ListItem>
        )}
        {hasRole(['sindico', 'sub_sindico', 'conselheiro', 'morador', 'zelador']) && (
          <ListItem button component={Link} to="/comunicados" className={styles.navbarList}>
            <ListItemIcon><AnnouncementIcon className={styles.navbarIcons} /></ListItemIcon>
            <ListItemText primary="Comunicados" className={styles.navbarList} />
          </ListItem>
        )}

        {/* --- MORADOR --- */}
        {hasRole(['sindico', 'sub_sindico', 'conselheiro', 'morador', 'zelador']) && (
          <>
            <Typography variant="caption" sx={{ ml: 2, mt: 2, mb: 1, display: 'block', color: 'rgba(255,255,255,0.5)', fontWeight: 'bold' }}>
              RESIDENCIAL
            </Typography>
            <ListItem button component={Link} to="/reclamacoes" className={styles.navbarList}>
              <ListItemIcon><ReportIcon className={styles.navbarIcons} /></ListItemIcon>
              <ListItemText primary="Reclamações" className={styles.navbarList} />
            </ListItem>

            {hasRole(['sindico', 'sub_sindico', 'conselheiro', 'morador']) && (
              <>
                <ListItem button component={Link} to="/reservar-areas" className={styles.navbarList}>
                  <ListItemIcon><EventAvailableIcon className={styles.navbarIcons} /></ListItemIcon>
                  <ListItemText primary="Reservas" className={styles.navbarList} />
                </ListItem>
                <ListItem button component={Link} to="/meus-prestadores" className={styles.navbarList}>
                  <ListItemIcon><BadgeIcon className={styles.navbarIcons} /></ListItemIcon>
                  <ListItemText primary="Meus Prestadores" className={styles.navbarList} />
                </ListItem>
              </>
            )}
          </>
        )}

        {/* --- GESTÃO --- */}
        {hasRole(['sindico', 'sub_sindico', 'conselheiro', 'zelador']) && (
          <>
            <Typography variant="caption" sx={{ ml: 2, mt: 2, mb: 1, display: 'block', color: 'rgba(255,255,255,0.5)', fontWeight: 'bold' }}>
              GESTÃO
            </Typography>

            {hasRole(['sindico', 'sub_sindico', 'conselheiro', 'zelador']) && (
              <ListItem button component={Link} to="/gerenciar-moradores" className={styles.navbarList}>
                <ListItemIcon><PeopleIcon className={styles.navbarIcons} /></ListItemIcon>
                <ListItemText primary="Moradores" className={styles.navbarList} />
              </ListItem>
            )}
            {hasRole(['sindico', 'sub_sindico', 'conselheiro']) && (
              <ListItem button component={Link} to="/financeiro" className={styles.navbarList}>
                <ListItemIcon><MonetizationOnIcon className={styles.navbarIcons} /></ListItemIcon>
                <ListItemText primary="Financeiro" className={styles.navbarList} />
              </ListItem>
            )}
            {hasRole(['sindico', 'sub_sindico', 'conselheiro']) && (
              <ListItem button component={Link} to="/documentos" className={styles.navbarList}>
                <ListItemIcon><DescriptionIcon className={styles.navbarIcons} /></ListItemIcon>
                <ListItemText primary="Documentos" className={styles.navbarList} />
              </ListItem>
            )}
            {hasRole(['sindico', 'sub_sindico', 'conselheiro', 'zelador']) && (
              <ListItem button component={Link} to="/gerenciar-areas" className={styles.navbarList}>
                <ListItemIcon><MeetingRoomIcon className={styles.navbarIcons} /></ListItemIcon>
                <ListItemText primary="Áreas Comuns" className={styles.navbarList} />
              </ListItem>
            )}
            {hasRole(['sindico', 'sub_sindico', 'conselheiro']) && (
              <ListItem button component={Link} to="/gerenciar-funcionarios" className={styles.navbarList}>
                <ListItemIcon><AssignmentIndIcon className={styles.navbarIcons} /></ListItemIcon>
                <ListItemText primary="Funcionários" className={styles.navbarList} />
              </ListItem>
            )}
          </>
        )}

        {/* --- OPERACIONAL --- */}
        {(hasRole(['sindico', 'sub_sindico', 'conselheiro', 'zelador', 'funcionario', 'administradora'])) && (
          <>
            <Typography variant="caption" sx={{ ml: 2, mt: 2, mb: 1, display: 'block', color: 'rgba(255,255,255,0.5)', fontWeight: 'bold' }}>
              OPERACIONAL
            </Typography>

            <ListItem button component={Link} to="/controle-estoque" className={styles.navbarList}>
              <ListItemIcon><InventoryIcon className={styles.navbarIcons} /></ListItemIcon>
              <ListItemText primary="Estoque/Patrimônio" className={styles.navbarList} />
            </ListItem>

            {hasRole(['admin_sistema', 'administradora', 'sindico']) && (
              <ListItem button component={Link} to="/gerenciar-cotacoes" className={styles.navbarList}>
                <ListItemIcon><DescriptionIcon className={styles.navbarIcons} /></ListItemIcon>
                <ListItemText primary="Cotações" className={styles.navbarList} />
              </ListItem>
            )}

            {hasRole(['admin_sistema', 'administradora', 'sindico', 'zelador']) && (
              <ListItem button component={Link} to="/gerenciar-ordens" className={styles.navbarList}>
                <ListItemIcon><FactCheckIcon className={styles.navbarIcons} /></ListItemIcon>
                <ListItemText primary="Ordens de Compra" className={styles.navbarList} />
              </ListItem>
            )}

            {hasRole(['sindico', 'sub_sindico', 'conselheiro']) && (
              <ListItem button component={Link} to="/gerenciar-ponto" className={styles.navbarList}>
                <ListItemIcon><PunchClockIcon className={styles.navbarIcons} /></ListItemIcon>
                <ListItemText primary="Espelho de Ponto" className={styles.navbarList} />
              </ListItem>
            )}

            {hasRole(['funcionario', 'zelador']) && (
              <>
                <ListItem button component={Link} to="/controle-ponto" className={styles.navbarList}>
                  <ListItemIcon><PunchClockIcon className={styles.navbarIcons} /></ListItemIcon>
                  <ListItemText primary="Bater Ponto" className={styles.navbarList} />
                </ListItem>
                <ListItem button component={Link} to="/controle-acesso" className={styles.navbarList}>
                  <ListItemIcon><BadgeIcon className={styles.navbarIcons} /></ListItemIcon>
                  <ListItemText primary="Controle Acesso" className={styles.navbarList} />
                </ListItem>
              </>
            )}
          </>
        )}

        {/* LINKS PARA ADMIN SISTEMA */}
        {userData && userData.papel === 'admin_sistema' && (
          <>
            <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
            <ListItem button component={Link} to="/gerenciar-condominios" className={styles.navbarList}>
              <ListItemIcon><HomeIcon className={styles.navbarIcons} /></ListItemIcon>
              <ListItemText primary="Gerenciar Condomínios" className={styles.navbarList} />
            </ListItem>
            <ListItem button component={Link} to="/gerenciar-fornecedores" className={styles.navbarList}>
              <ListItemIcon><PeopleIcon className={styles.navbarIcons} /></ListItemIcon>
              <ListItemText primary="Gerenciar Fornecedores" className={styles.navbarList} />
            </ListItem>
          </>
        )}

      </List>

      <Box sx={{ marginTop: 'auto', p: 2 }}>
        <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
        {userData && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40, mr: 2 }}>
              {userData.foto_url ? <img src={userData.foto_url} alt={userData.nome} style={{ width: '100%', height: '100%' }} /> : <AccountCircleIcon />}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold' }}>{userData.nome}</Typography>
                <IconButton size="small" onClick={handleEditProfile} sx={{ color: 'rgba(255,255,255,0.7)', p: 0.5 }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {userData.papel}{userData.apto ? ` | Apto: ${userData.apto}` : ''}
              </Typography>
            </Box>
          </Box>
        )}
        <Button
          onClick={handleLogout}
          startIcon={<ExitToAppIcon />}
          sx={{
            color: 'white',
            justifyContent: 'flex-start',
            textTransform: 'none',
            width: '100%'
          }}
        >
          Sair
        </Button>
      </Box>
    </div>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          display: { sm: 'none' }, // Mostra a barra superior apenas em telas pequenas (mobile/tablet)
          background: 'linear-gradient(90deg, #283593 0%, #1976d2 100%)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Predioo
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="menu folders"
      >
        {/* Drawer Mobile (Temporário) */}
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Melhora o desempenho em mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: 'linear-gradient(180deg, #283593 0%, #1976d2 100%)',
              color: 'white'
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Drawer Desktop (Permanente) */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: 'linear-gradient(180deg, #283593 0%, #1976d2 100%)',
              color: 'white'
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Edit Profile Dialog */}
      <Dialog open={openProfileDialog} onClose={() => setOpenProfileDialog(false)}>
        <DialogTitle>Editar Perfil</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Seu Nome"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProfileDialog(false)}>Cancelar</Button>
          <Button onClick={handleSaveProfile} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;